import { usePortfolioSelectedOrder } from 'portfolio/store/hooks';
import { useAppSelector, useDispatch } from 'store/hooks';
import {
    clearData,
    selectEstimatedTradeCostState,
} from 'widgets/estimatedTradeCost/store/data/dataSlice';
import { useEstimatedTradeCostWidgetConfigSlice } from 'widgets/estimatedTradeCost/widget/store/configslice';
import {
    EstimatedTradeCostRequest,
    toUid,
} from 'widgets/estimatedTradeCost/store/data/EstimatedTradeCostRequest';
import { EstimatedTradeCostOrderDataState } from 'widgets/estimatedTradeCost/store/data/EstimateTradeCostDataState';
import {
    useCheckWidgetStream,
    useClearResponse,
} from 'widgets/streaming/hooks';
import { useCallback, useEffect, useState } from 'react';
import {
    EstimatedTradeCostComponentState,
    useEstimatedTradeCostWidgetComponentSlice,
} from 'widgets/estimatedTradeCost/widget/store/componentSlice';
import { toRequest } from 'widgets/estimatedTradeCost/store/data/mapping';
import { useDispatchUpdateWidgetComponentState } from 'widgets/store/component/dispatcher';
import { EstimatedTradeCostComparisonComponentState } from 'widgets/estimatedTradeCost/widget-comparison/store/componentSlice';

export function useEstimatedTradeCostDataRequestUid() {
    const selectedOrder = usePortfolioSelectedOrder();
    const { config } = useEstimatedTradeCostWidgetConfigSlice();
    const { component } = useEstimatedTradeCostWidgetComponentSlice();

    const request = toRequest(selectedOrder, config, component);
    return request ? toUid(request) : undefined;
}

export function useEstimatedTradeCostData():
    | EstimatedTradeCostOrderDataState
    | undefined {
    const uid = useEstimatedTradeCostDataRequestUid();
    return useAppSelector((s) => selectEstimatedTradeCostState(s, uid ?? ''));
}

export function useReRunEstimatedTradeCostRequest() {
    const uid = useEstimatedTradeCostDataRequestUid();
    const clearRestResponse = useClearResponse(uid);
    const dispatcher = useDispatch();
    const checkWidgetStream = useCheckWidgetStream();

    return useCallback(() => {
        if (uid) {
            clearRestResponse();
            dispatcher(clearData(uid));
            setTimeout(() => {
                checkWidgetStream();
            }, 0);
        }
    }, [clearRestResponse, checkWidgetStream, dispatcher, uid]);
}

export function useEditableQuantity(
    request: EstimatedTradeCostRequest | undefined
) {
    const selectedOrder = usePortfolioSelectedOrder();
    const specifiedQuantity = request?.specifiedQty;
    const dispatchComponentChanged = useDispatchUpdateWidgetComponentState<
        | EstimatedTradeCostComponentState
        | EstimatedTradeCostComparisonComponentState
    >();

    const [editedQty, setEditedQty] = useState<undefined | number>(undefined);

    useEffect(() => {
        setEditedQty(undefined);
    }, [setEditedQty, selectedOrder?.orderID]);

    const onQtyChanged = useCallback(
        (nextQty: number) => {
            setEditedQty(nextQty);
        },
        [setEditedQty]
    );

    const isQuantityDirty = !!(editedQty && editedQty !== specifiedQuantity);

    const dispatchEditedQuantity = () => {
        if (editedQty) {
            dispatchComponentChanged({ qty: editedQty });
            setEditedQty(undefined);
        }
    };

    return { editedQty, onQtyChanged, isQuantityDirty, dispatchEditedQuantity };
}
